/**
 * Carbon progress bar for quotas and gauges: a 14px label over an 8px bar
 * (the strong neutral on a layer-accent track) and an optional helper line.
 * `status="error"` is for a blocking state only (a quota exhausted): the fill,
 * the octagon and the helper turn Coral. A progressbar for assistive tech;
 * other props (`data-testid`, say) land on the root.
 */
import type { HTMLAttributes, ReactNode } from 'react'
import { useId } from 'react'
import clsx from 'clsx'
import { IconSeverityCrit } from './icons'

export interface ProgressBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  value: number
  max: number
  label: ReactNode
  helper?: ReactNode
  status?: 'active' | 'error'
  /** Keep the label for screen readers only. */
  hideLabel?: boolean
}

export function ProgressBar({ value, max, label, helper, status = 'active', hideLabel, className, ...rest }: ProgressBarProps) {
  const id = useId()
  const safeMax = max > 0 ? max : 1
  const clamped = Math.min(safeMax, Math.max(0, Number.isFinite(value) ? value : 0))
  const error = status === 'error'
  return (
    <div className={clsx('min-w-0', className)} {...rest}>
      <div id={`${id}-label`} className={clsx(hideLabel ? 'sr-only' : 'mb-2 flex items-center gap-2 text-body text-text-primary')}>
        {label}
        {error ? <IconSeverityCrit className="text-sev-crit" /> : null}
      </div>
      <div
        role="progressbar"
        aria-labelledby={`${id}-label`}
        aria-describedby={helper ? `${id}-helper` : undefined}
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        className="h-2 w-full bg-layer-accent"
      >
        <div className={clsx('h-full', error ? 'bg-sev-crit' : 'bg-text-primary')} style={{ width: `${(clamped / safeMax) * 100}%` }} />
      </div>
      {helper ? (
        <p id={`${id}-helper`} className={clsx('mt-2 text-helper', error ? 'text-crit-ink' : 'text-text-helper')}>
          {helper}
        </p>
      ) : null}
    </div>
  )
}

export default ProgressBar
